import React, { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  HttpError,
  useNavigation,
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";
import { Bell, ChevronDown, FolderInput } from "lucide-react";
import moment from "moment";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { useJustCreated } from "@/components/common/enrichment-async-hint";
import { RefreshTracesCard } from "@/components/common/refresh-traces";
import JsonEditor from "@/components/fields/json-editor";
import { photosDiffer } from "@/components/forms/schemas/media";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { convertToTimezone, replaceTimezone } from "@/lib/dateUtils";
import { getDirtyPaths, isFormDirty, withClearedFields } from "@/lib/utils";
import EventSummary from "@/pages/events/components/event-summary";
import { AccordionWrapperField } from "@/pages/events/components/fields/accordion-wrapper-field";
import { AppliesToTicketingField } from "@/pages/events/components/fields/applies-to-ticketing-field";
import { EventTagsField } from "@/pages/events/components/fields/event-tags-field";
import { LineupFormField } from "@/pages/events/components/fields/lineup-field";
import { MapsUrlField } from "@/pages/events/components/fields/maps-url-field";
import { PromoCodesField } from "@/pages/events/components/fields/promo-codes-field";
import { ProviderSelectField } from "@/pages/events/components/fields/provider-select-field";
import { ReferenceIdsField } from "@/pages/events/components/fields/reference-ids-field";
import { SlugField } from "@/pages/events/components/fields/slug-field";
import { TextOverridesField } from "@/pages/events/components/fields/text-overrides-field";
import { TicketingUpdatedAtField } from "@/pages/events/components/fields/ticketing-updated-at-field";
import { VenueSelectField } from "@/pages/events/components/fields/venue-select-field";
import { VenueTbaField } from "@/pages/events/components/fields/venue-tba-field";
import { EventUpdatesOptionsDropdown } from "@/pages/events/features/notifications/components/event-updates-options";
import { useEventUpdates } from "@/pages/events/features/notifications/event-updates-context";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";
import {
  EventEditFormType,
  eventEditSchema,
  eventEditSubmitSchema,
} from "@/pages/events/schemas/event.edit.form";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";

import { stampCuratorPriceEdits } from "./utils/stamp-curator-price-edits";

// Re-exports for backward compatibility
export { eventEditSchema as eventSchema } from "@/pages/events/schemas/event.edit.form";

const schemaProvider = new ZodProvider(eventEditSubmitSchema);

/**
 * Fields a curator may clear, i.e. the ones the API accepts `""` for. There is
 * no `UpdateEventCommandValidator`, and the handler `Set()`s `About` straight
 * through. `mapsUrl` is excluded on purpose — it is `.url()`-typed and a stored
 * empty URL is not something we want — as are `status`/`type` (enums, `""`
 * fails JSON binding → 400) and the required strings (`name`, `slug`,
 * `timezone`).
 */
const CLEARABLE_FIELDS = ["about"] as const;

export const EventsEdit = ({ clone }: { clone?: boolean }) => {
  const { id } = useParsed();

  const [refresh, setRefresh] = React.useState(false);
  const [values, setValues] = React.useState<EventEditFormType>();

  const { open } = useNotification();
  const setTitle = useDocumentTitle();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const { list } = useNavigation();
  const justCreated = useJustCreated();

  const {
    artists,
    setArtists,
    setVenue,
    setPendingUpdate,
    openConfirmationModal,
  } = useEventUpdates();

  const { setLoading } = useLoadingOverlay();
  const { update, get, exportOrders } = useEventRepository();

  // Load event data
  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "No event ID provided",
        description: "Please provide an event ID to edit.",
      });
      return;
    }

    setLoading(true);
    get(id.toString())
      .then((data) => {
        data.startDate = data.startDate
          ? convertToTimezone(
              moment(data.startDate).toDate(),
              data.timezone,
            ).toISOString()
          : data.startDate;
        data.endDate = data.endDate
          ? convertToTimezone(
              moment(data.endDate).toDate(),
              data.timezone,
            ).toISOString()
          : data.endDate;
        data.doorsTime = data.doorsTime
          ? convertToTimezone(
              moment(data.doorsTime).toDate(),
              data.timezone,
            ).toISOString()
          : data.doorsTime;

        if (clone) {
          delete data.id;
        }
        setTitle(data.name);
        setValues(data);

        if (data.venue) {
          setVenue(data.venue.id);
        }
        setArtists(
          data.lineup
            ?.filter((l) => !!l.id)
            .map((l) => ({
              id: l.id ?? "",
              name: l.name,
              followers: l.followers,
            })) ?? [],
        );
      })
      .catch((error) => {
        open?.({
          type: "error",
          message: "Failed to load event data",
          description:
            (error as HttpError).message ||
            "An error occurred while fetching the event data.",
        });
        console.error("Error fetching event data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, refresh]);

  const initializeForm = (
    form: UseFormReturn<EventEditFormType, any, EventEditFormType>,
  ) => {
    form.watch((currentValues) => {
      setWarnWhen(
        isFormDirty(
          currentValues as Record<string, unknown>,
          currentValues as Record<string, unknown>,
          form.formState.defaultValues as Record<string, unknown>,
        ),
      );
    });
  };

  const handleUpdates = (
    valuesToSubmit: EventEditFormType,
    form: UseFormReturn<EventEditFormType, any, EventEditFormType>,
  ) => {
    const initialValues = values;
    let shouldSendUpdate = true;

    const dirtyFields = form.formState.dirtyFields;
    const dirtyPaths = getDirtyPaths(dirtyFields);

    if (
      dirtyPaths.includes("lineup") &&
      dirtyFields.lineup!.length >
        (form.formState.defaultValues?.lineup?.length ?? 0)
    ) {
      setPendingUpdate({ type: "lineup_added" });
      setArtists(
        form
          .getValues()
          .lineup?.filter((l) => !!l?.id)
          .map((l: any) => ({
            id: l.id ?? "",
            name: l.name,
            followers: l.followers,
          })) ?? [],
      );
    } else if (dirtyPaths.includes("ticketing")) {
      setPendingUpdate({ type: "tickets_on_sale" });
    } else if (
      dirtyPaths.includes("offers") &&
      (valuesToSubmit.offers?.length ?? 0) >
        (initialValues?.offers?.length || 0)
    ) {
      setPendingUpdate({ type: "offer_added_v2" });
    } else if (dirtyPaths.includes("venue") && valuesToSubmit.venue) {
      if (valuesToSubmit.venue.id) setVenue(valuesToSubmit.venue.id);
      setPendingUpdate({ type: "venue_changed" });
    } else if (dirtyPaths.includes("startDate")) {
      setPendingUpdate({ type: "date_changed" });
    } else {
      shouldSendUpdate = false;
    }
    return shouldSendUpdate;
  };

  const handleSubmit = async (
    data: EventEditFormType,
    form: UseFormReturn<EventEditFormType, any, EventEditFormType>,
  ) => {
    data.startDate = replaceTimezone(
      moment(data.startDate).toDate(),
      data.timezone,
    ).toISOString();
    data.endDate = data.endDate
      ? replaceTimezone(
          moment(data.endDate).toDate(),
          data.timezone,
        ).toISOString()
      : data.endDate;
    data.doorsTime = data.doorsTime
      ? replaceTimezone(
          moment(data.doorsTime).toDate(),
          data.timezone,
        ).toISOString()
      : data.doorsTime;

    setWarnWhen(false);
    // AutoForm strips emptied fields before we get here — re-add the ones the
    // curator cleared on purpose as "", which the API stores as an empty value.
    const payload = withClearedFields<EventEditFormType>(
      data,
      form.getValues() as Record<string, unknown>,
      form.formState.defaultValues as Record<string, unknown>,
      CLEARABLE_FIELDS,
    ) as EventEditFormType;
    if (payload.tickets) {
      payload.tickets = stampCuratorPriceEdits(
        values?.tickets ?? [],
        payload.tickets,
        new Date(),
      );
    }
    await update(payload, {
      clearPlaceholderArt: photosDiffer(
        values?.media?.photos,
        data.media?.photos,
      ),
    });
    setValues(undefined);
    setRefresh((prev) => !prev); // trigger data reload
    if (handleUpdates(data, form)) openConfirmationModal();
  };

  return (
    <div className="flex md:flex-row flex-col">
      <div className="w-full md:w-3/4 p-8">
        {values && (
          <AutoForm
            key={JSON.stringify(values)}
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={handleSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents({
              formComponents: {
                slugField: SlugField,
                venueSelect: VenueSelectField,
                mapsUrl: MapsUrlField,
                venueTba: VenueTbaField,
                lineup: LineupFormField,
                providerSelect: ProviderSelectField,
                promoCodes: PromoCodesField,
                textOverrides: TextOverridesField,
                appliesToTicketing: AppliesToTicketingField,
                referenceIds: ReferenceIdsField,
                ticketingUpdatedAt: TicketingUpdatedAtField,
                eventTags: EventTagsField,
                accordionWrapper: AccordionWrapperField,
              },
              uiComponents: {},
            })}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
              <div className="flex flex-1 justify-end space-x-2">
                {values?.ticketing && (
                  <Button
                    type="button"
                    className="space-x-1"
                    onClick={() => {
                      exportOrders(values as EventEditFormType);
                    }}
                  >
                    <FolderInput className="h-4 w-4" />
                    <p>Export Orders</p>
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() =>
                    window.open(
                      `https://events.musicofourdesire.com/event/${values.slug}`,
                      "_blank",
                    )
                  }
                >
                  View in web
                </Button>
                <JsonEditor
                  json={values}
                  onChanged={(json) => {
                    try {
                      setValues(eventEditSchema.parse(json));
                    } catch (ex) {
                      open?.({
                        message: "Failed to validate custom json",
                        description: String(ex),
                        type: "error",
                      });
                    }
                  }}
                />
                <EventUpdatesOptionsDropdown
                  followers={values?.interestedIn ?? 0}
                  thirdPartyFollowers={
                    (artists?.reduce(
                      (acc, prev) => (prev.followers ?? 0) + acc,
                      0,
                    ) ?? 0) + (values?.venue?.followers ?? 0)
                  }
                >
                  <Button className="gap-2">
                    <Bell className="h-4 w-4" />
                    Send Update
                    <ChevronDown />
                  </Button>
                </EventUpdatesOptionsDropdown>
                <DeleteButton
                  resource="events"
                  onSuccess={() => {
                    list("events");
                  }}
                />
              </div>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
      <div className="w-full md:w-1/4 lg:w-2/6 pt-8 md:sticky md:top-8 md:mb-14 md:z-15 md:h-full min-h-[calc(100vh-108px)] flex flex-col gap-6">
        {values && (
          <EventSummary
            data={values}
            justCreated={justCreated}
            // A group write changes the event on the server only, so re-read it.
            onGroupChanged={() => setRefresh((prev) => !prev)}
            // That re-read rebuilds the form, which would drop unsaved edits.
            groupLocked={warnWhen}
          />
        )}
        {values && (
          <EntityTinaTrace
            entityType="event"
            entityId={values.id}
            metadata={values.metadata}
            enrichment={values.enrichment}
          />
        )}
        {/* Under the Tina trace, because it answers the same question one step
            further out: the trace is the item that published this event, and
            this is every refresh that has run against it since. Fetches the
            event's own refresh jobs from Tina, and renders nothing for an event
            that has never been refreshed. */}
        {values && <RefreshTracesCard eventId={values.id} />}
      </div>
    </div>
  );
};
